import { useId } from "react";
import type { InterpolatedHeadFrame } from "./frame-interpolation";
import type { HeadState, Trace } from "./model";

interface ShotGraphProps {
  heads: Record<1 | 2, HeadState>;
  showPlateauMarkers?: boolean;
  displayedFrames?: Record<1 | 2, InterpolatedHeadFrame>;
}

export interface FlowPoint {
  elapsedMs: number;
  millilitersPerSecond: number;
}

const WIDTH = 720;
const HEIGHT = 282;
const PAD = { left: 45, right: 18, top: 15, bottom: 18 };
const FRAME_MS = 250;
const SMOOTHING = 0.34;

export function toFlowPoints(trace?: Trace): FlowPoint[] {
  if (!trace?.points.length) return [];
  let previousElapsedMs = 0;
  let previousMilliliters = 0;
  let smooth = 0;
  return trace.points.map((point) => {
    const elapsedMs = Math.max(1, point.elapsedMs - previousElapsedMs);
    const deltaMilliliters = Math.max(0, point.milliliters - previousMilliliters);
    const raw = deltaMilliliters * 1000 / elapsedMs;
    smooth = raw === 0 ? 0 : smooth + SMOOTHING * (raw - smooth);
    previousElapsedMs = point.elapsedMs;
    previousMilliliters = point.milliliters;
    return { elapsedMs: point.elapsedMs, millilitersPerSecond: smooth };
  });
}

export function roundedFlowScale(value: number): number {
  if (value <= 1) return 1;
  if (value <= 2) return 2;
  if (value <= 5) return 5;
  if (value <= 10) return 10;
  if (value <= 20) return 20;
  return Math.ceil(value / 10) * 10;
}

export function graphWindowMs(traces: Array<Trace | undefined>): number {
  const longest = Math.max(0, ...traces.map((trace) => trace?.points.at(-1)?.elapsedMs ?? 0));
  const frames = Math.min(260, Math.max(140, Math.ceil(longest / FRAME_MS)));
  return frames * FRAME_MS;
}

export function plateauMarkerFrames(startMs: number, endMs: number): [number, number] {
  const startFrame = startMs / FRAME_MS;
  return [startFrame, Math.max(startFrame, endMs / FRAME_MS - 1)];
}

function xForFrame(frameIndex: number, frameCount: number): number {
  const plotWidth = WIDTH - PAD.left - PAD.right;
  return PAD.left + Math.max(0, Math.min(frameCount - 1, frameIndex)) * plotWidth / Math.max(1, frameCount - 1);
}

function yForFlow(flow: number, maxFlow: number): number {
  const plotHeight = HEIGHT - PAD.top - PAD.bottom;
  return HEIGHT - PAD.bottom - Math.max(0, Math.min(1, flow / maxFlow)) * plotHeight;
}

function tangentAt(points: FlowPoint[], index: number): number {
  if (points.length < 2) return 0;
  if (index === 0) return points[1]!.millilitersPerSecond - points[0]!.millilitersPerSecond;
  if (index + 1 >= points.length) return points.at(-1)!.millilitersPerSecond - points.at(-2)!.millilitersPerSecond;
  const before = points[index]!.millilitersPerSecond - points[index - 1]!.millilitersPerSecond;
  const after = points[index + 1]!.millilitersPerSecond - points[index]!.millilitersPerSecond;
  return before * after <= 0 ? 0 : 2 * before * after / (before + after);
}

export function shapePreservingPath(points: FlowPoint[], maxTime: number, maxFlow: number): string {
  const frameCount = Math.max(1, Math.round(maxTime / FRAME_MS));
  const visible = points.filter((point) => point.elapsedMs <= maxTime);
  if (!visible.length) return "";
  const coordinates = visible.map((point) => ({
    x: xForFrame(point.elapsedMs / FRAME_MS - 1, frameCount),
    y: yForFlow(point.millilitersPerSecond, maxFlow),
  }));
  let path = `M${coordinates[0]!.x.toFixed(1)},${coordinates[0]!.y.toFixed(1)}`;
  for (let index = 0; index + 1 < coordinates.length; index += 1) {
    const start = visible[index]!.millilitersPerSecond;
    const end = visible[index + 1]!.millilitersPerSecond;
    const startTangent = tangentAt(visible, index);
    const endTangent = tangentAt(visible, index + 1);
    const startPoint = coordinates[index]!;
    const endPoint = coordinates[index + 1]!;
    const deltaX = endPoint.x - startPoint.x;
    const control1Y = yForFlow(Math.max(Math.min(start + startTangent / 3, Math.max(start, end)), Math.min(start, end)), maxFlow);
    const control2Y = yForFlow(Math.max(Math.min(end - endTangent / 3, Math.max(start, end)), Math.min(start, end)), maxFlow);
    path += ` C${(startPoint.x + deltaX / 3).toFixed(1)},${control1Y.toFixed(1)} ${(endPoint.x - deltaX / 3).toFixed(1)},${control2Y.toFixed(1)} ${endPoint.x.toFixed(1)},${endPoint.y.toFixed(1)}`;
  }
  return path;
}

export function visibleFlowPoints(points: FlowPoint[], visibleFrames?: number): FlowPoint[] {
  if (visibleFrames === undefined) return points;
  const count = Math.max(0, Math.min(points.length, visibleFrames));
  if (count < 1) return [];
  const wholePoints = points.slice(0, Math.floor(count));
  const next = points[Math.floor(count)];
  if (!next || wholePoints.length === 0) return wholePoints;
  const previous = wholePoints.at(-1)!;
  const fraction = count - Math.floor(count);
  if (fraction <= 0) return wholePoints;
  return [...wholePoints, {
    elapsedMs: previous.elapsedMs + (next.elapsedMs - previous.elapsedMs) * fraction,
    millilitersPerSecond: previous.millilitersPerSecond + (next.millilitersPerSecond - previous.millilitersPerSecond) * fraction,
  }];
}

export function traceClassForStatus(status: HeadState["status"], normalClass: string): string {
  return status === "flush_ignored" || status === "incomplete" || status === "sensor_fault"
    ? "trace trace--error"
    : `trace ${normalClass}`;
}

/**
 * Builds the touchscreen's red comparison area. It is deliberately limited to
 * the common detected plateau: prewet, startup peaks, cutoff, and unread live
 * frames are not part of the comparison.
 */
export function mismatchFillPath(active: FlowPoint[], activeTrace: Trace, reference: FlowPoint[], referenceTrace: Trace, maxTime: number, maxFlow: number, visibleFrames?: number): string {
  if (
    activeTrace.plateauStartMs === undefined || activeTrace.plateauEndMs === undefined ||
    referenceTrace.plateauStartMs === undefined || referenceTrace.plateauEndMs === undefined ||
    active.length === 0 || reference.length === 0
  ) return "";

  const frameCount = Math.max(1, Math.round(maxTime / FRAME_MS));
  const plateauStart = Math.ceil(Math.max(activeTrace.plateauStartMs, referenceTrace.plateauStartMs) / FRAME_MS);
  const plateauEnd = Math.floor(Math.min(activeTrace.plateauEndMs, referenceTrace.plateauEndMs) / FRAME_MS);
  const visibleEnd = Math.min(active.length - 1, reference.length - 1, plateauEnd - 1, (visibleFrames === undefined ? active.length : Math.max(0, Math.min(active.length, visibleFrames))) - 1);
  if (plateauStart > visibleEnd) return "";

  const rateAt = (points: FlowPoint[], frame: number) => {
    const before = Math.floor(frame);
    const after = Math.min(before + 1, points.length - 1);
    const fraction = frame - before;
    return points[before]!.millilitersPerSecond + (points[after]!.millilitersPerSecond - points[before]!.millilitersPerSecond) * fraction;
  };
  const pointAt = (points: FlowPoint[], frame: number) => ({
    x: xForFrame(frame, frameCount),
    y: yForFlow(rateAt(points, frame), maxFlow),
  });
  const frames = Array.from({ length: Math.floor(visibleEnd) - plateauStart + 1 }, (_, index) => plateauStart + index);
  if (visibleEnd > Math.floor(visibleEnd)) frames.push(visibleEnd);
  const activePoints = frames.map((frame) => pointAt(active, frame));
  const referencePoints = frames.map((frame) => pointAt(reference, frame)).reverse();
  const coordinates = [...activePoints, ...referencePoints];
  return coordinates.length < 3 ? "" : `M${coordinates.map(({ x, y }) => `${x.toFixed(1)},${y.toFixed(1)}`).join(" L")} Z`;
}

export function ShotGraph({ heads, showPlateauMarkers = false, displayedFrames }: ShotGraphProps) {
  const titleId = useId();
  const descriptionId = useId();
  const traces = [heads[1].trace, heads[2].trace, heads[1].reference, heads[2].reference];
  const maxTime = graphWindowMs(traces);
  const flows = {
    h1: toFlowPoints(heads[1].trace),
    h2: toFlowPoints(heads[2].trace),
    r1: toFlowPoints(heads[1].reference),
    r2: toFlowPoints(heads[2].reference),
  };
  const maxFlow = roundedFlowScale(Math.max(
    1,
    ...flows.h1.map((point) => point.millilitersPerSecond),
    ...flows.h2.map((point) => point.millilitersPerSecond),
    ...flows.r1.map((point) => point.millilitersPerSecond),
    ...flows.r2.map((point) => point.millilitersPerSecond),
  ));
  const referenceFlow = flows.r1.length ? flows.r1 : flows.r2;
  const displayedFlows = {
    h1: visibleFlowPoints(flows.h1, displayedFrames?.[1].visibleFrames),
    h2: visibleFlowPoints(flows.h2, displayedFrames?.[2].visibleFrames),
  };
  const mismatchFills = ([1, 2] as const).map((head) => mismatchFillPath(
    flows[`h${head}`],
    heads[head].trace,
    flows[`r${head}`],
    heads[head].reference ?? { points: [] },
    maxTime,
    maxFlow,
    displayedFrames?.[head].visibleFrames,
  ));
  const hasData = displayedFlows.h1.length > 0 || displayedFlows.h2.length > 0 || referenceFlow.length > 0;
  const plotWidth = WIDTH - PAD.left - PAD.right;
  const plotHeight = HEIGHT - PAD.top - PAD.bottom;
  const plateauTraces: Array<{ trace?: Trace; className: string }> = [
    { trace: heads[1].reference, className: "plateau-marker plateau-marker--reference" },
    { trace: heads[1].trace, className: "plateau-marker plateau-marker--h1" },
    { trace: heads[2].trace, className: "plateau-marker plateau-marker--h2" },
  ];

  // Portrait CSS matches the touchscreen plot ratio. Compact landscape keeps
  // preserveAspectRatio="none" so every hardware band remains on one screen;
  // only the time axis stretches in that constrained orientation.

  return (
    <div className="graph-wrap">
      <div className="graph-key" aria-hidden="true">
        <span><i className="swatch swatch--h1" />H1</span>
        <span><i className="swatch swatch--h2" />H2</span>
        <span><i className="swatch swatch--reference" />REFERENCE</span>
      </div>
      <svg
        className="shot-graph"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        aria-labelledby={`${titleId} ${descriptionId}`}
        preserveAspectRatio="none"
      >
        <title id={titleId}>Shot flow-rate graph</title>
        <desc id={descriptionId}>Smoothed milliliters per second over shot time for heads one and two, with the loaded reference in gray. Red fill marks flow differences inside overlapping detected plateaus.</desc>
        <defs>
          <pattern id="grid" width={plotWidth / 8} height={plotHeight / 4} patternUnits="userSpaceOnUse">
            <path d={`M ${plotWidth / 8} 0 L 0 0 0 ${plotHeight / 4}`} fill="none" stroke="#292a26" strokeWidth="1" />
          </pattern>
        </defs>
        <rect x={PAD.left} y={PAD.top} width={plotWidth} height={plotHeight} fill="url(#grid)" />
        {[0, 1, 2, 3, 4].map((line) => {
          const value = maxFlow * (4 - line) / 4;
          const y = PAD.top + plotHeight * line / 4;
          return <text key={line} className="axis-label" x={PAD.left - 9} y={y + 3} textAnchor="end">{maxFlow < 10 ? value.toFixed(1) : Math.round(value)}</text>;
        })}
        <text className="axis-label axis-label--unit" x="4" y={PAD.top + 8}>mL/s</text>
        <text className="axis-label" x={PAD.left} y={HEIGHT - 12}>0</text>
        <text className="axis-label" x={WIDTH - PAD.right} y={HEIGHT - 12} textAnchor="end">{Math.round(maxTime / 1000)}s</text>
        {mismatchFills.map((path, index) => path ? <path key={index} className="mismatch-fill" d={path} /> : null)}
        {showPlateauMarkers ? plateauTraces.flatMap(({ trace, className }, traceIndex) => {
          if (trace?.plateauStartMs === undefined || trace.plateauEndMs === undefined) return [];
          return plateauMarkerFrames(trace.plateauStartMs, trace.plateauEndMs).map((frame, markerIndex) => {
            const x = xForFrame(frame, Math.round(maxTime / FRAME_MS));
            return <line key={`${traceIndex}-${markerIndex}`} className={className} x1={x} x2={x} y1={PAD.top} y2={HEIGHT - PAD.bottom} />;
          });
        }) : null}
        {referenceFlow.length ? <path className="trace trace--reference" d={shapePreservingPath(referenceFlow, maxTime, maxFlow)} /> : null}
        {displayedFlows.h1.length ? <path className={traceClassForStatus(heads[1].status, "trace--h1")} d={shapePreservingPath(displayedFlows.h1, maxTime, maxFlow)} /> : null}
        {displayedFlows.h2.length ? <path className={traceClassForStatus(heads[2].status, "trace--h2")} d={shapePreservingPath(displayedFlows.h2, maxTime, maxFlow)} /> : null}
      </svg>
      {!hasData ? <div className="graph-empty"><strong>READY</strong><span>Pair the controller or run a demo shot.</span></div> : null}
    </div>
  );
}
